/**
 * RateHawk API Integration Service
 * 
 * Calls through local proxy server (server.js) which handles
 * authentication and forwards requests to RateHawk SANDBOX API
 */

import { PROXY_API_PATH } from '../constants';
import { getApiLanguage } from '../i18n';

const BASE_URL = PROXY_API_PATH;

class RateHawkService {
    /**
     * Autocomplete Search - Proxied
     * @param {string} query - The search query (minimum 2 characters)
     * @param {string} language - Language code (default: 'en')
     */
    async autocomplete(query, language = getApiLanguage()) {
        if (!query || query.length < 2) {
            return { hotels: [], regions: [] };
        }

        try {
            const response = await fetch(`${BASE_URL}/autocomplete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: query,
                    language: language
                })
            });

            if (!response.ok) {
                return { hotels: [], regions: [] };
            }

            const data = await response.json();

            if (data.status === 'error') {
                console.error('[RateHawk] API error:', data.error);
                return { hotels: [], regions: [] };
            }

            return {
                hotels: data.data?.hotels || [],
                regions: data.data?.regions || []
            };
        } catch (error) {
            console.error('[RateHawk] Error:', error.message);
            return { hotels: [], regions: [] };
        }
    }

    /**
     * Hotel Search — routes by geo coords or region ID
     * @param {object} params - Search params (checkin, checkout, guests, etc.)
     *   Pass latitude+longitude for geo search, or region_id for region search.
     */
    async startSearch(params) {
        const endpoint = (params.latitude && params.longitude) ? '/geo-search' : '/search';
        try {
            const response = await fetch(`${BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.status === 'error') throw new Error(data.error || 'Search failed');
            return data.data || {};
        } catch (error) {
            console.error('[RateHawk] startSearch error:', error.message);
            return {};
        }
    }

    /**
     * Price a specific list of hotels by ID for given dates — powers the /hotels
     * listing's live "from" prices (RateHawk serp/hotels via the proxy /search-hotels).
     * One call prices all the supplied IDs.
     * @param {object}   params
     * @param {string[]} params.ids        - Hotel slug IDs to price
     * @param {string}   params.checkin    - YYYY-MM-DD
     * @param {string}   params.checkout   - YYYY-MM-DD
     * @param {Array}    params.guests     - [{ adults, children:[ages] }] (one entry per room)
     * @param {string}   [params.residency]- ISO country code, default server 'gb'
     * @param {string}   [params.currency] - e.g. 'PKR', default server 'USD'
     * @param {string}   [params.language] - default current app language
     * @returns {Promise<object[]>} hotels[] each with rates[]; [] on error/empty
     */
    async searchHotelsByIds({ ids, checkin, checkout, guests, residency, currency, language = getApiLanguage(), signal }) {
        if (!ids?.length || !checkin || !checkout) return [];
        try {
            const response = await fetch(`${BASE_URL}/search-hotels`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids, checkin, checkout, guests, residency, currency, language }),
                signal,
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.status === 'error') throw new Error(data.error || 'Search failed');
            return data.data?.hotels || [];
        } catch (error) {
            console.error('[RateHawk] searchHotelsByIds error:', error.message);
            return [];
        }
    }

    /**
     * Cancel a confirmed booking
     * @param {string} partner_order_id - The order ID used when creating the booking
     * @returns {Promise<{amount_refunded: object}>} Refund info
     */
    async cancelBooking(partner_order_id) {
        if (!partner_order_id) throw new Error('Order ID is required');
        const response = await fetch(`${BASE_URL}/cancel-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ partner_order_id }),
        });
        const data = await response.json();
        if (!response.ok || data.status === 'error') {
            throw new Error(data.error || `HTTP ${response.status}`);
        }
        return data.data || {};
    }

    /**
     * Retrieve rate info for a specific book_hash
     * @param {string} book_hash - The rate hash from search results
     * @param {string} language  - Language code, default 'en'
     */
    async getRateInfo(book_hash, language = getApiLanguage()) {
        if (!book_hash) throw new Error('book_hash is required');
        const response = await fetch(`${BASE_URL}/rate-info`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ book_hash, language }),
        });
        const data = await response.json();
        if (!response.ok || data.status === 'error') {
            throw new Error(data.error || `HTTP ${response.status}`);
        }
        return data.data || {};
    }

    /**
     * Download voucher PDF for a completed booking
     * Triggers a browser file download automatically.
     * @param {string} partner_order_id - The booking order ID
     * @param {string} language         - Language for the PDF, default 'en'
     */
    async downloadVoucher(partner_order_id, language = getApiLanguage()) {
        if (!partner_order_id) throw new Error('Order ID is required');
        const url = `${BASE_URL}/voucher?partner_order_id=${encodeURIComponent(partner_order_id)}&language=${language}`;
        const response = await fetch(url);
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `voucher-${partner_order_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
    }

    /**
     * Content API – Get hotel IDs filtered by property type, country, star rating, amenities
     * @param {object} filters
     * @param {string[]} [filters.kind]        - e.g. ['Hotel'], ['Resort'], ['Hostel']
     * @param {number[]} [filters.country]     - country numeric IDs e.g. [189] for UAE
     * @param {number[]} [filters.star_rating] - e.g. [4, 5]
     * @param {string[]} [filters.serp_filters]- e.g. ['has_pool', 'has_breakfast']
     * @param {boolean}  [filters.top]         - only top-rated properties
     * @param {boolean}  [filters.preferable]  - only preferred properties
     * @returns {Promise<{ids: string[], hids: number[]}>} slug IDs for FastAPI + numeric hids for reviews
     */
    async getHotelIdsByFilter({ kind, country, star_rating, serp_filters, top, preferable } = {}) {
        try {
            const response = await fetch(`${BASE_URL}/content/hotel-ids-by-filter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kind, country, star_rating, serp_filters, top, preferable })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.status === 'error') throw new Error(data.error);
            return {
                ids:  data.data?.ids  || [],
                hids: data.data?.hids || [],
            };
        } catch (error) {
            console.error('[RateHawk] getHotelIdsByFilter error:', error.message);
            return { ids: [], hids: [] };
        }
    }

    /**
     * Get static content for a single hotel by its string ID
     * @param {string} id       - Hotel slug e.g. "best_western_london"
     * @param {string} language - Language code default 'en'
     * @returns {Promise<object|null>} Hotel content object or null
     */
    /**
     * Get reviews for a batch of hotels by their numeric HID
     * @param {number[]} hids     - Array of numeric hotel IDs
     * @param {string}   language - Language code, default 'en'
     * @returns {Promise<Object>} Map of hid → reviews[]
     */
    async getHotelReviewsByIds(hids = [], language = getApiLanguage()) {
        const cleanHids = [...new Set(
            hids
                .map(Number)
                .filter(Number.isFinite)
                .filter(n => n > 0)
        )];
        if (!cleanHids.length) return {};
        try {
            const response = await fetch(`${BASE_URL}/content/hotel-reviews-by-ids`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ hids: cleanHids, language })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            const map = {};
            for (const entry of (data?.data || [])) {
                if (entry?.hid != null) map[entry.hid] = entry.reviews || [];
            }
            return map;
        } catch (error) {
            console.error('[RateHawk] getHotelReviewsByIds error:', error.message);
            return {};
        }
    }

    async getHotelInfo(id, language = getApiLanguage()) {
        try {
            const response = await fetch(`${BASE_URL}/hotel-info`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, language })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            // hotel-info endpoint returns: { data: { hotels: [ {...} ] } }
            return data?.data?.hotels?.[0] || data?.data || null;
        } catch (error) {
            console.error('[RateHawk] getHotelInfo error:', error.message);
            return null;
        }
    }
}

export const rateHawkService = new RateHawkService();
